import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import { LanguageSlugEnum } from '@/types/regionTypes';

import type {
  AccountError,
  AddressFragment,
  AddressTypeEnum,
  MetadataItem,
} from '@/graphql/saleor/api';
import type { UseCheckout } from '@/hooks/useCheckout';
import {
  addressSetDefault,
  addressToInput,
  getMetaValue,
  isSameAddress,
} from '@/lib/address';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import JP_PREFECTURE from '@/lib/prefecture.json';

type CheckoutAddressMirror = {
  readonly billingAddress?: AddressFragment | null;
  readonly id?: string;
  readonly shippingAddress?: AddressFragment | null;
} | null;

export type CommonSetAsPrimaryAddressArgs = {
  address: AddressFragment;
  addressTitleUpdate?: UseCheckout['addressTitleUpdate'];
  addressUpdate?: UseCheckout['addressUpdate'];
  billingTitle?: string;
  checkout?: CheckoutAddressMirror;
  id: string;
  prevDefaultBilling?: AddressFragment | null;
  prevDefaultShipping?: AddressFragment | null;
  shippingTitle?: string;
  type: AddressTypeEnum;
};

export const commonSetAsPrimaryAddressLogic = async ({
  address,
  addressTitleUpdate,
  addressUpdate,
  billingTitle,
  checkout,
  id,
  prevDefaultBilling,
  prevDefaultShipping,
  shippingTitle,
  type,
}: CommonSetAsPrimaryAddressArgs) => {
  let errors: AccountError[] = [];
  const res = await addressSetDefault({ id, type });
  errors = res.errors;

  if (!addressTitleUpdate || !addressUpdate || !isEmpty(errors)) {
    return errors;
  }

  // Only mirror the new primary onto the checkout if the checkout is currently
  // holding the previous primary on the affected side and the toggle is an
  // actual change. This preserves manual address picks made in the checkout
  // flow.
  const checkoutAddrT =
    type === 'SHIPPING' ? checkout?.shippingAddress : checkout?.billingAddress;
  const currentPrimaryT =
    type === 'SHIPPING' ? prevDefaultShipping : prevDefaultBilling;

  const shouldSync =
    !!checkout?.id &&
    !!checkoutAddrT &&
    !!currentPrimaryT &&
    isSameAddress(checkoutAddrT, currentPrimaryT, ['phone']) &&
    !isSameAddress(checkoutAddrT, address, ['phone']);

  if (!shouldSync) {
    return errors;
  }

  const newTitle = getMetaValue(
    ACCOUNT_METADATA.personalTitle,
    address.metadata as MetadataItem[],
  );
  const nextShippingTitle = type === 'SHIPPING' ? newTitle : shippingTitle;
  const nextBillingTitle = type === 'BILLING' ? newTitle : billingTitle;

  // Sequential: addressUpdate triggers an internal checkout refresh that
  // must see the already-committed title update to avoid stale reads.
  // Do not convert to Promise.all.
  await addressTitleUpdate(nextShippingTitle ?? 'Mr', nextBillingTitle ?? 'Mr');
  await addressUpdate(
    type.toLowerCase() as 'shipping' | 'billing',
    addressToInput({
      ...address,
      metadata: address.metadata.map((item: MetadataItem) =>
        pick(item, ['key', 'value']),
      ),
    }),
  );

  return errors;
};

type CheckoutDeleteMirror = {
  readonly billingAddress?: AddressFragment | null;
  readonly billingTitle?: string | null;
  readonly id?: string;
  readonly shippingAddress?: AddressFragment | null;
  readonly shippingTitle?: string | null;
} | null;

export type SyncCartAfterAddressDeleteParams = {
  addressBeingDeleted: AddressFragment | undefined;
  addressId: string;
  addressTitleUpdate: UseCheckout['addressTitleUpdate'];
  addressUpdate: UseCheckout['addressUpdate'];
  checkoutSnapshot: CheckoutDeleteMirror;
  countryCode: string;
  prevDefaultBilling?: AddressFragment | null;
  prevDefaultShipping?: AddressFragment | null;
  remainingAddresses: AddressFragment[];
};

// Syncs the Saleor checkout cart after an address is deleted.
// If the deleted address was on the cart (shipping or billing side), it picks
// the best available fallback and pushes the update + title sync to the cart.
export const syncCartAfterAddressDelete = async ({
  addressBeingDeleted,
  addressId,
  addressTitleUpdate,
  addressUpdate,
  checkoutSnapshot,
  countryCode,
  prevDefaultBilling,
  prevDefaultShipping,
  remainingAddresses,
}: SyncCartAfterAddressDeleteParams) => {
  // Saleor creates a NEW address record (with its own id) when attaching to
  // a checkout, so cart-side ids never match user-address ids. Compare by
  // content via isSameAddress, ignoring `phone` since Saleor sometimes
  // normalizes it.
  const isOnCheckoutShipping =
    !!checkoutSnapshot?.shippingAddress &&
    !!addressBeingDeleted &&
    isSameAddress(checkoutSnapshot.shippingAddress, addressBeingDeleted, [
      'phone',
    ]);
  const isOnCheckoutBilling =
    !!checkoutSnapshot?.billingAddress &&
    !!addressBeingDeleted &&
    isSameAddress(checkoutSnapshot.billingAddress, addressBeingDeleted, [
      'phone',
    ]);

  // Fallback priority per side: primary (if not deleted, country matches)
  // → first remaining address with matching country → null.
  const pickFallback = (
    side: 'SHIPPING' | 'BILLING',
  ): AddressFragment | null => {
    const primary =
      side === 'SHIPPING' ? prevDefaultShipping : prevDefaultBilling;
    if (
      primary &&
      primary.id !== addressId &&
      primary.country?.code === countryCode
    ) {
      return primary;
    }
    return (
      remainingAddresses.find((a) => a.country?.code === countryCode) ?? null
    );
  };

  const shippingFallback = isOnCheckoutShipping
    ? pickFallback('SHIPPING')
    : null;
  const billingFallback = isOnCheckoutBilling ? pickFallback('BILLING') : null;

  const stripMetadata = (address: AddressFragment) =>
    addressToInput({
      ...address,
      metadata: address.metadata
        .map((item: MetadataItem) => pick(item, ['key', 'value']))
        .filter((item) => item.key !== ACCOUNT_METADATA.addressSaved),
    });

  // Sequential: addressUpdate triggers an internal checkout refresh that
  // must see the already-committed title update to avoid stale reads.
  // Do not convert to Promise.all.
  if (shippingFallback) {
    await addressUpdate('shipping', stripMetadata(shippingFallback));
  }
  if (billingFallback) {
    await addressUpdate('billing', stripMetadata(billingFallback));
  }

  if (shippingFallback || billingFallback) {
    const newShippingTitle = shippingFallback
      ? getMetaValue(
          ACCOUNT_METADATA.personalTitle,
          shippingFallback.metadata as MetadataItem[],
        )
      : checkoutSnapshot?.shippingTitle;
    const newBillingTitle = billingFallback
      ? getMetaValue(
          ACCOUNT_METADATA.personalTitle,
          billingFallback.metadata as MetadataItem[],
        )
      : checkoutSnapshot?.billingTitle;
    await addressTitleUpdate(newShippingTitle || 'Mr', newBillingTitle || 'Mr');
  }
};

export const isLatin = (str: string) => /^[\p{Script=Latin}\s]+$/u.test(str);
export const isKanji = (str: string) => /^[\u4e00-\u9fa5]+$/.test(str);

export const showKanjiLabel = (languageCode?: string) => {
  return languageCode
    ? languageCode === LanguageSlugEnum.ja ||
        languageCode === LanguageSlugEnum.zh
    : false;
};

export const getPrefectureLabelByValue = (
  kanji: boolean,
  selectedValue?: string,
) => {
  if (!selectedValue) return '';
  const prefectureOptions = kanji
    ? JP_PREFECTURE.filter((item) => isKanji(item.label))
    : JP_PREFECTURE.filter((item) => isLatin(item.label));
  return (
    prefectureOptions.filter((item) => item.value === selectedValue)?.[0]
      ?.label ?? ''
  );
};
